"use client";

import { Box, Container, Divider, List, rem, Stack, Text, Title } from "@mantine/core";
import { ProtectedEmailLink } from "@/app/lib/components/ProtectedEmail";
import { CONTACT_EMAIL_BASE64 } from "@/app/lib/seo/siteConfig";

export default function TermsPage() {
  return (
    <Box py={{ base: 40, md: 80 }}>
      <Container size="md">
        <Stack gap="lg" px={{ base: "md", md: 0 }}>
          <Box mb="xl">
            <Title order={1} ta="center" mb="xs">
              Terms of Service
            </Title>
            <Text size="lg" c="gray.7" ta="center">
              Last updated: August 11, 2025
            </Text>
          </Box>

          {/* Introduction */}
          <Box>
            <Text size="md" c="gray.8" lh="lg">
              These Terms of Service ("Terms") govern your use of Orgtools ("Service") provided by
              Orgtools ("we," "us," or "our"). By accessing or using our Service, you agree to be
              bound by these Terms.
            </Text>
          </Box>

          <Divider />

          {/* 1. Service Description */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              1. Service Description
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              Orgtools is a decision-making platform that helps organizations streamline their
              decision-making processes through structured workflows, collaboration tools, and
              analytics.
            </Text>
            <Text size="md" c="gray.8" lh="lg">
              We reserve the right to modify, suspend, or discontinue any aspect of the Service at
              any time with reasonable notice to users.
            </Text>
          </Box>

          {/* 2. User Accounts */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              2. User Accounts and Responsibilities
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              You are responsible for:
            </Text>
            <List spacing="sm" size="md" c="gray.8">
              <List.Item>Maintaining the confidentiality of your account credentials</List.Item>
              <List.Item>All activities that occur under your account</List.Item>
              <List.Item>Ensuring your account information is accurate and up-to-date</List.Item>
              <List.Item>Notifying us immediately of any unauthorized access</List.Item>
            </List>
            <Text size="md" c="gray.8" lh="lg" mt="md">
              You must be at least 18 years old and have the authority to enter into these Terms on
              behalf of your organization.
            </Text>
          </Box>

          {/* 3. Acceptable Use */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              3. Acceptable Use Policy
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              You agree not to use the Service to:
            </Text>
            <List spacing="sm" size="md" c="gray.8">
              <List.Item>Violate any applicable laws or regulations</List.Item>
              <List.Item>Infringe on intellectual property rights</List.Item>
              <List.Item>Transmit malicious code or attempt to breach security</List.Item>
              <List.Item>Interfere with or disrupt the Service</List.Item>
              <List.Item>Use automated tools to access the Service without permission</List.Item>
              <List.Item>Share access credentials with unauthorized parties</List.Item>
            </List>
          </Box>

          {/* 4. Payment Terms */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              4. Payment and Billing
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              Subscription fees are billed in advance on a recurring basis. You authorize us to
              charge your payment method for all applicable fees.
            </Text>
            <List spacing="sm" size="md" c="gray.8">
              <List.Item>All fees are non-refundable except as required by law</List.Item>
              <List.Item>Prices may change with 30 days notice</List.Item>
              <List.Item>Failed payments may result in service suspension</List.Item>
              <List.Item>You're responsible for all taxes and fees</List.Item>
            </List>
          </Box>

          {/* 5. Data and Privacy */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              5. Data and Privacy
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              Your use of the Service is also governed by our Privacy Policy. You retain ownership
              of your data, and we process it according to our Privacy Policy and applicable data
              protection laws.
            </Text>
            <Text size="md" c="gray.8" lh="lg">
              You grant us the right to use your data solely to provide and improve the Service. We
              implement appropriate security measures to protect your data.
            </Text>
          </Box>

          {/* 6. AI Advisors and Model Outputs */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              6. AI Advisors and Model Outputs
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              The Service allows you to configure and interact with custom AI agents ("AI
              Advisors"). AI outputs are generated probabilistically and may be inaccurate,
              incomplete, misleading, delayed, or inappropriate.
            </Text>
            <List spacing="sm" size="md" c="gray.8">
              <List.Item>
                You are solely responsible for evaluating the accuracy, suitability, and legality of
                any output and for all decisions or actions taken in reliance on it.
              </List.Item>
              <List.Item>
                You must implement appropriate human review and controls, especially for high-impact
                or regulated uses.
              </List.Item>
              <List.Item>
                We do not guarantee that any output will meet your requirements or achieve any
                particular results.
              </List.Item>
            </List>
          </Box>

          {/* 7. No Professional Advice */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              7. No Professional Advice
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              The Service and any AI Advisors do not provide legal, financial, medical, accounting,
              human resources, safety, or other professional advice. Outputs are for informational
              purposes only and are not a substitute for advice from qualified professionals.
            </Text>
            <Text size="md" c="gray.8" lh="lg">
              No attorney-client, fiduciary, or other professional relationship is created by your
              use of the Service or any AI Advisor, even if an Advisor is labeled "Legal,"
              "Finance," or similar.
            </Text>
          </Box>

          {/* 8. User-Generated Advisors and Content */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              8. User-Generated Advisors and Content
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              You may create prompts, configurations, tools, integrations, training data, messages,
              files, and other materials for use with AI Advisors, and the Service may generate
              outputs for your organization (collectively, "Customer Content").
            </Text>
            <List spacing="sm" size="md" c="gray.8" mb="md">
              <List.Item>
                Ownership: You retain ownership of Customer Content, including your AI Advisor
                definitions and configurations.
              </List.Item>
              <List.Item>
                License to Us: You grant us a non-exclusive, worldwide, royalty-free license to
                host, run, process, transmit, display, and modify Customer Content as necessary to
                operate, secure, troubleshoot, and improve the Service, including generating outputs
                and applying safety systems.
              </List.Item>
              <List.Item>
                Sharing and Marketplace: If you share an AI Advisor outside your organization or
                publish it to any catalog or marketplace within the Service, you grant us and the
                receiving users a license to use, copy, and adapt that Advisor as enabled by the
                Service.
              </List.Item>
              <List.Item>
                Your Responsibilities: You represent and warrant that you have all rights and lawful
                bases required for the Customer Content you submit; that it does not infringe,
                misappropriate, or violate any rights or laws; and that you will not include
                confidential information or personal data without authorization and compliance with
                applicable law.
              </List.Item>
              <List.Item>
                You are responsible for the behavior of your AI Advisors and for all outputs
                generated for your accounts.
              </List.Item>
            </List>
            <Text size="sm" c="gray.7">
              As described in our Privacy Policy, we may use de-identified or aggregated data and
              usage metadata to improve the Service. We do not use Customer Content to train
              third-party foundation models by default.
            </Text>
          </Box>

          {/* 9. Third-Party AI and Integrations */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              9. Third-Party AI and Integrations
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              The Service may route prompts and data to third-party model providers and
              integrations. Use of such providers is subject to their terms and policies. We do not
              control third-party systems and disclaim responsibility for their acts, omissions, or
              outputs.
            </Text>
            <Text size="md" c="gray.8" lh="lg">
              Do not submit information to the Service that you are not authorized to disclose to
              such providers.
            </Text>
          </Box>

          {/* 10. Prohibited and High-Risk Uses */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              10. Prohibited and High-Risk Uses
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              In addition to the Acceptable Use Policy, you agree not to use the Service or AI
              Advisors for:
            </Text>
            <List spacing="sm" size="md" c="gray.8">
              <List.Item>
                Life-critical or safety-critical systems, emergency services, medical diagnosis or
                treatment, autonomous vehicles, aviation, nuclear, or other high-risk activities
                where failure could lead to harm.
              </List.Item>
              <List.Item>
                Generating or disseminating content intended to be taken as professional advice
                without qualified human review.
              </List.Item>
              <List.Item>
                Any use that violates law, rights of others, or model provider terms.
              </List.Item>
            </List>
          </Box>

          {/* 11. Moderation and Enforcement */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              11. Moderation and Enforcement
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              We may monitor for abuse and enforce policies, including reviewing, disabling, or
              removing AI Advisors or content, rate-limiting, filtering, or suspending access where
              we believe there is risk or a violation of these Terms.
            </Text>
          </Box>

          {/* 12. Indemnification */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              12. Indemnification
            </Title>
            <Text size="md" c="gray.8" lh="lg">
              You will defend, indemnify, and hold harmless Orgtools and our affiliates, officers,
              and employees from and against any claims, losses, liabilities, damages, costs, and
              expenses (including reasonable attorneys' fees) arising out of or relating to: (a)
              Customer Content; (b) your AI Advisors or their outputs; (c) your use of the Service
              in violation of these Terms or law; or (d) infringement or violation of third-party
              rights.
            </Text>
          </Box>

          {/* 13. Intellectual Property */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              13. Intellectual Property
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              The Service and its original content, features, and functionality are owned by us and
              protected by international copyright, trademark, and other intellectual property laws.
            </Text>
            <Text size="md" c="gray.8" lh="lg">
              You retain ownership of content you upload but grant us a license to use it as
              necessary to provide the Service.
            </Text>
          </Box>

          {/* 14. Service Availability */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              14. Service Availability
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              We strive to maintain high service availability but cannot guarantee uninterrupted
              access. We may perform maintenance that temporarily affects service availability.
            </Text>
            <Text size="md" c="gray.8" lh="lg">
              We are not liable for any disruption of service due to circumstances beyond our
              reasonable control.
            </Text>
          </Box>

          {/* 15. Limitation of Liability */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              15. Limitation of Liability
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF
              PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS INTERRUPTION, ARISING FROM OR RELATING
              TO YOUR USE OF THE SERVICE OR ANY AI OUTPUTS.
            </Text>
            <Text size="md" c="gray.8" lh="lg">
              Without limiting the foregoing, we are not liable for any decisions made or actions
              taken (or not taken) in reliance on AI outputs, or for any professional obligations
              you may have. Our total liability for all claims relating to the Service shall not
              exceed the amounts you paid for the Service in the 12 months preceding the claim.
            </Text>
          </Box>

          {/* 16. Termination */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              16. Termination
            </Title>
            <Text size="md" c="gray.8" lh="lg" mb="md">
              Either party may terminate this agreement at any time. Upon termination:
            </Text>
            <List spacing="sm" size="md" c="gray.8">
              <List.Item>Your access to the Service will cease</List.Item>
              <List.Item>
                We will provide reasonable opportunity to export your data (typically 30 days)
              </List.Item>
              <List.Item>We may delete your data after the export period</List.Item>
              <List.Item>Outstanding fees remain due</List.Item>
            </List>
          </Box>

          {/* 17. Governing Law */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              17. Governing Law
            </Title>
            <Text size="md" c="gray.8" lh="lg">
              These Terms are governed by the laws of the United States, without regard to conflict
              of law principles. Any disputes will be resolved in the courts of the United States.
            </Text>
          </Box>

          {/* 18. Changes to Terms */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              18. Changes to Terms
            </Title>
            <Text size="md" c="gray.8" lh="lg">
              We may update these Terms from time to time. We will notify you of material changes
              via email or through the Service. Continued use after changes constitutes acceptance
              of the new Terms.
            </Text>
          </Box>

          {/* 19. Contact Information */}
          <Box>
            <Title order={2} size={rem(24)} mb="md" c="gray.8">
              19. Contact Information
            </Title>
            <Text size="md" c="gray.8" lh="lg">
              If you have questions about these Terms, please contact us at:
            </Text>
            <Text mt="xs" fw={500}>
              Orgtools
              <br />
              10768 Rocky Peak Place
              <br />
              Gig Harbor, WA 98332
              <br />
              <ProtectedEmailLink address={CONTACT_EMAIL_BASE64.support} />
            </Text>
          </Box>

          <Divider />

          {/* Footer */}
          <Box ta="center" py="md">
            <Text size="sm" c="gray.6">
              These Terms of Service are effective as of August 11, 2025 and supersede all prior
              agreements.
            </Text>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
